"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireDefault(require("react"));
var _tssReact = require("tss-react");
var _colorManipulator = require("../styles/colorManipulator");
var _tss = require("../styles/tss");
var _useTheme = require("../styles/useTheme");
var _responsiveProps = require("../utilities/responsiveProps");
var _utils = require("./utils");
const _excluded = ["animation", "classes", "className", "component", "height", "variant", "width", "size"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const pulseKeyframes = (0, _tssReact.keyframes)`
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
`;
const waveKeyframes = (0, _tssReact.keyframes)`
  0% {
    transform: translateX(-100%);
  }

  60% {
    transform: translateX(100%);
  }

  100% {
    transform: translateX(100%);
  }
`;
const useStyles = (0, _tss.makeTssStyles)({
  name: 'DsSkeleton',
  uniqId: 'ZwyCdw'
})((theme, _params, classes) => {
  const {
    styles,
    colors,
    spacing
  } = theme.busbud;
  const sizeCssDefinitions = {
    size: {
      xs: {
        height: spacing(1),
        [`&.${classes.circular}`]: {
          width: spacing(1)
        }
      },
      sm: {
        height: spacing(2),
        [`&.${classes.circular}`]: {
          width: spacing(2)
        }
      },
      md: {
        height: spacing(3),
        [`&.${classes.circular}`]: {
          width: spacing(3)
        }
      },
      lg: {
        height: spacing(4),
        [`&.${classes.circular}`]: {
          width: spacing(4)
        }
      },
      xl: {
        height: spacing(5),
        [`&.${classes.circular}`]: {
          width: spacing(5)
        }
      }
    }
  };
  return _objectSpread({
    /* Styles applied to the root element. */
    root: {
      display: 'block',
      // Create a "on paper" color with sufficient contrast retaining the color
      backgroundColor: colors.component.skeleton.gradientTo,
      height: '1.2em'
    },
    /* Styles applied to the root element if `variant="text"`. */
    text: {
      marginTop: 0,
      marginBottom: 0,
      height: 'auto',
      transformOrigin: '0 55%',
      transform: 'scale(1, 0.60)',
      borderRadius: styles.rounding.sm,
      '&:empty:before': {
        content: '"\\00a0"'
      }
    },
    /* Styles applied to the root element if `variant="rectangular"`. */
    rectangular: {
      borderRadius: styles.rounding.sm
    },
    /* Styles applied to the root element if `variant="circular"`. */
    circular: {
      borderRadius: styles.rounding.circle
    },
    /* Styles applied to the root element if `animation="pulse"`. */
    pulse: {
      animation: `${pulseKeyframes} 1.5s ease-in-out 0.5s infinite`
    },
    /* Styles applied to the root element if `animation="wave"`. */
    wave: {
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        animation: `${waveKeyframes} 1.6s linear 0.5s infinite`,
        background: `linear-gradient(90deg, transparent, ${(0, _colorManipulator.alpha)(colors.component.skeleton.gradientFrom, 0.5)}, transparent)`,
        content: '""',
        position: 'absolute',
        transform: 'translateX(-100%)',
        // Avoid flash during server-side hydration
        bottom: 0,
        left: 0,
        right: 0,
        top: 0
      }
    },
    /* Styles applied when the component is passed children. */
    withChildren: {
      '& > *': {
        visibility: 'hidden'
      }
    },
    /* Styles applied when the component is passed children and no width. */
    fitContent: {
      maxWidth: 'fit-content'
    },
    /* Styles applied when the component is passed children and no height. */
    heightAuto: {
      height: 'auto'
    },
    rectangularSizeXs: {},
    rectangularSizeSm: {},
    rectangularSizeMd: {},
    rectangularSizeLg: {},
    rectangularSizeXl: {},
    circularSizeXs: {},
    circularSizeSm: {},
    circularSizeMd: {},
    circularSizeLg: {},
    circularSizeXl: {}
  }, (0, _responsiveProps.generateUnoptimizedResponsiveStyles)(theme, sizeCssDefinitions));
});
const getWidth = (variant, base, width) => {
  if (variant === 'circular' || width === undefined) return undefined;
  return (typeof width === 'string' ? parseFloat(width) : width) * base;
};

/**
 * Display a placeholder preview of your content before the data gets loaded to reduce load-time frustration.
 *
 * [Storybook](https://busbud.github.io/design-system/?path=/docs/components-inputs-skeleton--page)
 */
const Skeleton = /*#__PURE__*/_react.default.forwardRef(function Skeleton(props, ref) {
  const {
      animation = 'wave',
      classes: classesOverrides,
      className,
      component: Component = 'span',
      height,
      variant = 'rectangular',
      width,
      // base *8
      size = 'sm'
    } = props,
    other = (0, _objectWithoutProperties2.default)(props, _excluded);
  const {
    cx,
    classes
  } = useStyles(undefined, {
    props: {
      classes: classesOverrides
    }
  });
  const hasChildren = Boolean(other.children);
  const theme = (0, _useTheme.useTheme)();
  return /*#__PURE__*/_react.default.createElement(Component
  // @ts-ignore TS2590
  , (0, _extends2.default)({
    ref: ref,
    className: cx(classes.root, classes[variant], ...(0, _utils.getSizeClassKey)(size, classes), ...(0, _utils.getVariantSizeClassKey)(variant, size, classes), animation && classes[animation], {
      [classes.withChildren]: hasChildren,
      [classes.fitContent]: hasChildren && !width,
      [classes.heightAuto]: hasChildren && !height
    }, className)
  }, other, {
    style: {
      width: getWidth(variant, theme.busbud.space.base, width),
      height
    }
  }));
});
var _default = exports.default = Skeleton;